import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IGenerateReferralCodeRequest {
  customerAccountNo: string;
  customerType: string;
  customerName: string;
  userId: string;
}

interface IGenerateReferralCodeResponse {
  status?: boolean;
  message?: string;
  data?: {
    referralCode?: string;
    referralLink?: string;
    qrCodeUrl?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGenerateReferralCodeResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const generateReferralCodeAsync = createAsyncThunk(
  'generateReferralCode',
  async (
    {
      customerAccountNo,
      customerName,
      customerType,
      userId,
    }: IGenerateReferralCodeRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.post(
        `/referral/referrer/code`,
        {
          customerAccountNo,
          customerName,
          customerType,
          userId,
        },
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IGenerateReferralCodeResponse>;
      return rejectWithValue(error.response?.data);
    }
  }
);

export const generateReferralCodeSlice = createSlice({
  name: 'generateReferralCode',
  initialState,
  reducers: {
    generateReferralCodeReset: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateReferralCodeAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(generateReferralCodeAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGenerateReferralCodeResponse;
      })
      .addCase(generateReferralCodeAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGenerateReferralCodeResponse;
        state.error = action.error.message;
      });
  },
});

export const { generateReferralCodeReset } = generateReferralCodeSlice.actions;

export default generateReferralCodeSlice.reducer;
